import { AllocationGridState, AllocationGridAction, AllocationMember } from '../types/allocation';

// Initial state for the allocation grid view-model
export const initialState: AllocationGridState = {
  members: [],
  months: [],
  loading: false,
  error: null,
};

// Allocation grid reducer
// Centralized state transitions for member selection, soft-removal and per-week hour edits
export const allocationReducer = (
  state: AllocationGridState,
  action: AllocationGridAction
): AllocationGridState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };

    case 'SET_MEMBERS':
      // Replace entire members collection (e.g., after fetch)
      return {
        ...state,
        members: action.payload,
      };

    case 'SET_MONTHS':
      // Replace generated month columns (derived from project start date)
      return {
        ...state,
        months: action.payload,
      };

    case 'TOGGLE_MEMBER_SELECTION':
      // Toggle selection flag (used for bulk actions like remove)
      return {
        ...state,
        members: state.members.map(member =>
          member.id === action.payload
            ? { ...member, selected: !member.selected }
            : member
        ),
      };

    case 'SELECT_ALL_MEMBERS':
      // Select all active (non-removed) members
      return {
        ...state,
        members: state.members.map(member => ({
          ...member,
          selected: !member.removed,
        })),
      };

    case 'DESELECT_ALL_MEMBERS':
      // Clear all selections
      return {
        ...state,
        members: state.members.map(member => ({
          ...member,
          selected: false,
        })),
      };

    case 'REMOVE_SELECTED_MEMBERS':
      // Soft-remove: mark as removed but keep in state for potential undo/reporting
      return {
        ...state,
        members: state.members.map(member =>
          member.selected ? { ...member, removed: true, selected: false } : member
        ),
      };

    case 'UPDATE_MONTHLY_ALLOCATION':
      // Edit hours for a specific member and month (validated upstream)
      return {
        ...state,
        members: state.members.map(member =>
          member.id === action.payload.memberId
            ? {
                ...member,
                monthlyAllocations: {
                  ...member.monthlyAllocations,
                  [action.payload.monthStart]: action.payload.hours,
                },
              }
            : member
        ),
      };

    case 'COPY_MONTH_ALLOCATIONS': {
      // Copy every active member's hours from one month column into another,
      // capped at the same 160hrs/month limit the inputs enforce.
      const { fromMonthStart, toMonthStart } = action.payload;
      if (fromMonthStart === toMonthStart) {
        return state;
      }

      return {
        ...state,
        members: state.members.map(member => {
          if (member.removed) return member;

          const hours = Math.min(member.monthlyAllocations[fromMonthStart] || 0, 160);
          return {
            ...member,
            monthlyAllocations: {
              ...member.monthlyAllocations,
              [toMonthStart]: hours,
            },
          };
        }),
      };
    }

    case 'UPDATE_HOURLY_COST':
      // Update hourly cost for a specific member
      return {
        ...state,
        members: state.members.map(member =>
          member.id === action.payload.memberId
            ? {
                ...member,
                member: {
                  ...member.member,
                  per_hour_cost: action.payload.cost,
                },
              }
            : member
        ),
      };

    case 'UPDATE_CLIENT_HOURLY_RATE':
      // Update the rate billed to the client for a specific member
      return {
        ...state,
        members: state.members.map(member =>
          member.id === action.payload.memberId
            ? { ...member, clientHourlyRate: action.payload.rate }
            : member
        ),
      };

    case 'ADD_MEMBER':
      // Append a newly added member to the grid
      return {
        ...state,
        members: [...state.members, action.payload],
      };

    case 'REMOVE_MEMBER':
      // Soft-remove a single member by id
      return {
        ...state,
        members: state.members.map(member =>
          member.id === action.payload
            ? { ...member, removed: true, selected: false }
            : member
        ),
      };

    default:
      return state;
  }
};

// Helper function to validate monthly allocation (≤ 160 hours/month per employee, ~40 hours/week * 4 weeks)
// Returns false if the input would exceed the per-month cap for the member
export const validateMonthlyAllocation = (
  memberId: number,
  monthStart: string,
  hours: number,
  members: AllocationMember[]
): boolean => {
  if (!Number.isInteger(hours) || hours < 0 || hours > 160) {
    return false;
  }

  // Check if this member already has allocations for this month
  const member = members.find(m => m.id === memberId);
  if (member) {
    const currentHours = member.monthlyAllocations[monthStart] || 0;
    // const totalHours = currentHours + hours;
    if (currentHours > 160) {
      return false;
    }
  }

  return true;
};

// Helper function to get total monthly hours for a member (defaults to 0)
export const getMemberMonthlyHours = (
  member: AllocationMember,
  monthStart: string
): number => {
  return member.monthlyAllocations[monthStart] || 0;
};

// Helper function to get selected members count (ignoring removed)
export const getSelectedMembersCount = (members: AllocationMember[]): number => {
  return members.filter(member => member.selected && !member.removed).length;
};

// Helper function to get active members (not removed)
export const getActiveMembers = (members: AllocationMember[]): AllocationMember[] => {
  return members.filter(member => !member.removed);
};
